import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, video, audio, iframe, [tabindex], [contenteditable]';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();

    // Only the active slide's video should ever play or download — mirrors the
    // hover-video pattern in product-card.js's #handleSlideshowSelect. `current`
    // is already set synchronously by both setup paths above, so this covers the
    // initial slide even though #setupSlideshowWithoutControls (0-1 slides) never
    // dispatches slideshow:select.
    this.#syncSlideVideos();
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideVideoSync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideVideoSync);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      this.removeEventListener('keydown', this.#handleKeyDown);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideVideoSync = (event) => {
    // SlideshowSelectEvent bubbles, and nested slideshow-component instances
    // are a supported pattern (see `isNested`) — without this check, a
    // descendant slideshow selecting its own slide would also reach this
    // ancestor's listener, and #syncSlideVideos would compare the
    // descendant's slide against this instance's own slides by identity,
    // never match, and wrongly pause this instance's currently-playing video.
    if (event.target !== this) return;
    this.#syncSlideVideos(event.detail.slide);
  };

  /**
   * Plays the video (if any) of every slide shown with the given one and pauses every other
   * top-level slide's video, so a video only plays — and only keeps downloading past
   * `preload: none` — while its slide is actually on screen.
   * @param {Element | undefined} [activeSlide]
   */
  #syncSlideVideos(activeSlide = this.refs.slides?.[this.current]) {
    if (!this.refs.slides) return;

    const shownSlides = this.#slidesShownWith(activeSlide);

    for (const slide of this.refs.slides) {
      // querySelector alone would also match a video that belongs to a
      // slideshow-component nested inside this slide (see `isNested`) — that
      // instance manages its own video playback, so only claim a video whose
      // nearest slideshow-component ancestor is this one.
      const video = [...slide.querySelectorAll('video')].find((el) => el.closest('slideshow-component') === this);
      if (!(video instanceof HTMLVideoElement)) continue;

      if (shownSlides.includes(slide)) {
        video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    }
  }

  /**
   * The slides on screen once the given slide is selected: that slide and, where a view holds
   * more than one (the two-column gallery on desktop), the ones after it. The count comes from
   * the slide's size against the scroller's rather than `visibleSlides`, which mid-scroll also
   * counts slides that are only passing by.
   * @param {Element | undefined} activeSlide
   * @returns {Element[]}
   */
  #slidesShownWith(activeSlide) {
    const { slides } = this;
    const index = activeSlide && slides ? slides.indexOf(activeSlide) : -1;
    if (!slides || index < 0) return activeSlide ? [activeSlide] : [];

    const side = this.#scroll?.axis === 'y' ? 'height' : 'width';
    const slideSize = activeSlide.getBoundingClientRect()[side];
    const perView = slideSize > 0 ? Math.round(this.refs.scroller.getBoundingClientRect()[side] / slideSize) : 1;

    return slides.slice(index, index + Math.max(1, perView));
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    const token = ++this.#selectToken;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // A true wrap (past the last/first slide), not an arbitrary far jump.
    const isWrap = requestedIndex > lastIndex || requestedIndex < 0;
    const wrapClone =
      !instant && isWrap && this.singleStep
        ? requestedIndex > lastIndex
          ? this.#endClones[0]
          : this.#startClones[this.#startClones.length - 1]
        : undefined;

    if (wrapClone) {
      // Animate to the clone (real, reachable content), then snap invisibly
      // to the real slide - avoids the instant cut at the scroller's edges.
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll
      if (token !== this.#selectToken) return;

      const realSlide = slides[index];
      if (!realSlide) return;

      const previousIndex = this.current;

      realSlide.setAttribute('aria-hidden', 'false');
      this.#scroll.to(wrapClone, { instant });
      this.current = index;
      this.#centerSelectedThumbnail(index, 'smooth');

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: event != null,
          trigger: 'select',
          slide: realSlide,
          id: realSlide.getAttribute('slide-id'),
        }),
      );

      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;
        if (token !== this.#selectToken) return;
        this.#scroll.to(realSlide, { instant: true });
      });

      return;
    }

    // If jump is more than 1 or we looped, do the placeholder + reorder trick.
    if (!instant && !isAdjacentSlide && (visibleSlides.length === 1 || this.singleStep)) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll
      if (token !== this.#selectToken) return;

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;
        if (token !== this.#selectToken) return;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    this.#syncSuspended = true;

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      }),
    );

    await this.#scroll?.finished;
    // A newer select() call may have interrupted this scroll (e.g. rapid
    // next/previous clicks) - its own `.to()` resolves this stale `finished`
    // early, so without this check we'd clear #syncSuspended out from under
    // the newer call's still-in-progress transition and let #sync() read
    // transient mid-scroll geometry, flickering the index.
    if (token !== this.#selectToken) return;
    this.#syncSuspended = false;
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Re-checks whether the track overflows and updates `auto-hide-controls`
   * visibility accordingly. The internal ResizeObserver only watches the
   * slideshow-container's own box, so it never fires when slides are shown
   * or hidden externally (e.g. a filter) without changing that box's size —
   * call this after doing so.
   */
  refreshControlsVisibility() {
    this.#updateControlsVisibility();
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    const prevDisabled = Boolean(!this.infinite && value === 0);
    const nextDisabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);

    if (previous) previous.disabled = prevDisabled;
    if (next) next.disabled = nextDisabled;

    for (const arrows of this.#externalArrows) {
      const extPrev = /** @type {HTMLButtonElement|null} */ (arrows.querySelector('.slideshow-control--previous'));
      const extNext = /** @type {HTMLButtonElement|null} */ (arrows.querySelector('.slideshow-control--next'));
      if (extPrev) extPrev.disabled = prevDisabled;
      if (extNext) extNext.disabled = nextDisabled;
    }
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  /** When set, next/previous always move by a single slide, regardless of visible count. */
  get singleStep() {
    return this.getAttribute('single-step') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = this.singleStep ? 1 : visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = this.singleStep ? 1 : visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' ||
      (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches) ||
      (this.hasAttribute('desktop-disabled') && mediaQueryLarge.matches) ||
      this.hasAttribute('nested-disabled')
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * True while select() is animating to a target it already knows - blocks #sync() from overriding it mid-scroll.
   * @type {boolean}
   */
  #syncSuspended = false;

  /**
   * Bumped at the start of every select() call. A call that resumes after an
   * `await` checks this against the token it captured to detect whether a
   * newer call has since superseded it - e.g. clicking next/previous again
   * before the current transition settles resolves the in-flight scroll's
   * `finished` promise early, so the interrupted call must not go on to
   * touch `current`/`#syncSuspended`/`#disabled` as if it were still current.
   * @type {number}
   */
  #selectToken = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /** Number of leading/trailing real slides cloned for seamless looping when `singleStep` is set. */
  #CLONE_COUNT = 2;

  /** @type {HTMLElement[]} Clones of the last slides, prepended before the first real slide. */
  #startClones = [];

  /** @type {HTMLElement[]} Clones of the first slides, appended after the last real slide. */
  #endClones = [];

  /**
   * Returns external slideshow-arrows elements linked to this component via
   * `data-slideshow-id` matching this element's `id`.
   */
  get #externalArrows() {
    if (!this.id) return [];
    return [...document.querySelectorAll(`slideshow-arrows[data-slideshow-id="${this.id}"]`)];
  }

  /**
   * Returns all slideshow-arrows: internal (inside this component, excluding
   * any belonging to a nested slideshow-component) + external.
   */
  get #allArrows() {
    const internalArrows = [...this.querySelectorAll('slideshow-arrows')].filter(
      (arrows) => arrows.closest('slideshow-component') === this,
    );
    return [...internalArrows, ...this.#externalArrows];
  }

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    this.#setupLoopClones();

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    // Block drag when nested, without hiding arrows like `disabled="true"` does.
    if (this.isNested) {
      this.setAttribute('nested-disabled', '');
    }

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        const updated = this.#updateVisibleSlides();
        if (updated !== visibleSlidesAmount) {
          visibleSlidesAmount = updated;
          this.current = this.#current;
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      }),
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
    this.#correctLoopClonePosition();
  };

  /**
   * The real index a loop clone stands in for, if closer than any real slide.
   * @returns {number | undefined}
   */
  #closestCloneIndex = () => {
    if (!this.#scroll) return undefined;

    const clones = [...this.#startClones, ...this.#endClones];
    if (!clones.length) return undefined;

    const { slides } = this;
    if (!slides?.length) return undefined;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    // Multi-up carousels (single-step, e.g. a two-column carousel) always
    // show more than one slide at rest, so the viewport's own center sits
    // between slides rather than on either one - use its leading edge
    // instead, same reference #sync() uses for that case. Based on the
    // `singleStep` design flag rather than the live `visibleSlides` count:
    // that count is transiently 2 mid-drag even for single-up galleries
    // (adjacent slides briefly both cross the visibility threshold), and
    // switching landmarks on that noise mid-transition is what causes the
    // index to jump around instead of settling smoothly.
    const referencePoint = this.singleStep ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    // Measure slides in the same frame as the reference: leading edge against
    // leading edge when single-step, center against center otherwise. Mixing a
    // slide's center with the scroller's edge left the slide at rest and its
    // predecessor at the same half-slide distance, a tie that hid a wrap onto
    // the first clone from this check.
    /** @param {Element} element */
    const positionOf = (element) => (this.singleStep ? element.getBoundingClientRect()[axis] : center(element, axis));

    const cloneDistances = clones.map((clone) => Math.abs(positionOf(clone) - referencePoint));
    const closestCloneDistance = Math.min(...cloneDistances);
    const closestClone = clones[cloneDistances.indexOf(closestCloneDistance)];
    if (!closestClone) return undefined;

    const closestRealDistance = Math.min(...slides.map((slide) => Math.abs(positionOf(slide) - referencePoint)));

    // Only act if a clone is strictly closer than every real slide.
    if (closestCloneDistance >= closestRealDistance) return undefined;

    // Each clone stands in for one specific real slide: the start clones copy
    // the last `#CLONE_COUNT` slides in order, the end clones the first ones.
    const startIndex = this.#startClones.indexOf(closestClone);
    if (startIndex !== -1) return slides.length - this.#startClones.length + startIndex;
    return this.#endClones.indexOf(closestClone);
  };

  /**
   * After any scroll settles, snaps back if resting on a loop clone (e.g. a
   * swipe past the real start/end - the only way to wrap on mobile, since
   * there are no arrows there).
   */
  #correctLoopClonePosition = () => {
    if (this.#disabled || !this.#scroll) return;

    const index = this.#closestCloneIndex();
    if (index === undefined) return;

    const realSlide = this.slides?.[index];
    if (!realSlide) return;

    this.#scroll.to(realSlide, { instant: true });
    this.current = index;
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    // A running transition already set the correct index; mid-scroll geometry here is transient.
    if (this.#disabled || this.#syncSuspended) return this.current;

    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const cloneIndex = this.#closestCloneIndex();
    if (cloneIndex !== undefined) return (this.current = cloneIndex);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    // See #closestCloneIndex for why this is keyed off `singleStep` and not
    // the live `visibleSlides.length`.
    const referencePoint = this.singleStep ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    // Don't intercept pointerdown on native interactive controls — calling preventDefault
    // below would otherwise block their default behaviour (e.g. opening a <select> dropdown).
    // Links (<a>) are intentionally excluded from this check: preventDefault on mousedown
    // only suppresses the browser's native link-drag ghost, NOT the subsequent click event,
    // so simple clicks still navigate. The drag path already intercepts the click (line ~670).
    if (event.target.closest('select, input, textarea, button, label, summary, [contenteditable="true"]')) {
      return;
    }

    // Let media-zoom handle its own click/mousemove interaction on desktop.
    if (event.target.closest('media-zoom')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        }),
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (this.disabled) return;
    if (
      event.target instanceof HTMLElement &&
      event.target.closest('input, textarea, select, [contenteditable="true"]')
    )
      return;

    event.stopPropagation();

    if (event.key === 'ArrowLeft') {
      this.previous(event);
    } else {
      this.next(event);
    }
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    const notScrollable = scroller.scrollWidth <= scroller.offsetWidth;

    if (slideshowControls instanceof HTMLElement) {
      slideshowControls.hidden = notScrollable;
    }

    // Also hide every `<slideshow-arrows>` when the track doesn't overflow —
    // both in-container arrows and the external inline-header arrows (linked via
    // `data-slideshow-id`). `#allArrows` covers internal + external; paired with
    // `slideshow-arrows[hidden] { display: none }` in base.css.
    for (const arrows of this.#allArrows) {
      arrows.toggleAttribute('hidden', notScrollable);
    }
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      },
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /** Clones the first/last `#CLONE_COUNT` real slides past the opposite end of the track, so wraps have real content to animate through. Only used when `infinite` and `singleStep` are both set. */
  #setupLoopClones() {
    if (!this.infinite || !this.singleStep) return;

    const { slides, scroller } = this.refs;
    const count = this.#CLONE_COUNT;
    if (!slides || slides.length <= count) return;

    /** @param {HTMLElement} slide */
    const cloneOf = (slide) => {
      const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
      clone.removeAttribute('ref');
      clone.removeAttribute('id');
      clone.removeAttribute('slide-id');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-loop-clone', '');
      clone.querySelectorAll('[on\\:click]').forEach((el) => el.removeAttribute('on:click'));
      clone.querySelectorAll(FOCUSABLE_SELECTOR).forEach((el) => el.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('video[autoplay]').forEach((el) => el.removeAttribute('autoplay'));
      return clone;
    };

    this.#startClones = slides.slice(-count).map(cloneOf);
    this.#endClones = slides.slice(0, count).map(cloneOf);

    scroller.prepend(...this.#startClones);
    scroller.append(...this.#endClones);

    // Correct the rest position - the prepended clones shifted it.
    const firstSlide = slides[0];
    if (firstSlide) this.#scroll?.to(firstSlide, { instant: true });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    // Keep `current` (and therefore dots/thumbnails aria-selected) in sync with
    // visibility as reported by the IntersectionObserver, since this fires
    // independently of the scroller's native 'scroll' event.
    this.#sync();

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
